import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Plus, Check, ChevronDown, ChevronRight, Clock,
  AlertTriangle, CheckCircle2, Circle, GripVertical, UserPlus, Crown, X,
} from 'lucide-react';
import { T, ROLES, springs } from '../lib/tokens';
import { supabase } from '../lib/supabase';
import { getMembers, cachedMembers } from '../lib/members';
import { Glass } from '../components/glass/Glass';
import { Pill, RoleBadge } from '../components/primitives/Badge';
import { ProgressBar } from '../components/primitives/ProgressBar';
import { TaskRow } from '../components/cards/TaskRow';
import { Avatar, displayName } from '../components/cards/AssigneePicker';

const PRIORITY_MAP = {
  1: { label: 'Critical', color: T.red },
  2: { label: 'High',     color: T.amber },
  3: { label: 'Medium',   color: T.blue },
  4: { label: 'Low',      color: T.textMuted },
};

// ─── Stage Timeline ─────────────────────────────────────────────────────────

const StageTimeline = ({ stages }) => {
  if (stages.length === 0) return null;

  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
      {stages.map((stage, i) => {
        const isCompleted = stage.stage_status === 'completed' || stage.done;
        const isActive = stage.is_active || stage.stage_status === 'active';
        const color = isCompleted ? T.green : isActive ? T.blue : 'rgba(0,0,0,0.08)';
        const textColor = isCompleted ? T.green : isActive ? T.blue : T.textLight;

        return (
          <div key={stage.id} style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{
              height: 6, borderRadius: 3, background: color,
              transition: 'background 0.3s ease',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {isCompleted ? <CheckCircle2 size={10} color={T.green} /> :
               isActive ? <Circle size={10} color={T.blue} fill={T.blue} /> :
               <Circle size={10} color={T.textLight} />}
              <span style={{
                fontSize: 10, fontWeight: isActive ? 700 : 500, color: textColor,
                letterSpacing: '0.02em',
              }}>{stage.title}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Inline Stage Task Input ────────────────────────────────────────────────

const StageTaskInput = ({ stageId, goalId, roleId, onCreated }) => {
  const [value, setValue] = useState('');
  const [duration, setDuration] = useState(30);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!value.trim()) return;
    setCreating(true);
    await supabase.from('tasks').insert({
      title: value.trim(),
      goal_id: goalId,
      milestone_id: stageId,
      role_id: roleId || null,
      priority: 2,
      duration_minutes: duration,
      status: 'todo',
      done: false,
      auto_schedule: true,
      sort_order: 0,
    });
    setValue('');
    setCreating(false);
    onCreated();
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
      borderRadius: 10, background: 'rgba(255,255,255,0.5)',
      border: '1px dashed rgba(0,0,0,0.10)', marginTop: 8,
    }}>
      <Plus size={14} color={T.textLight} style={{ flexShrink: 0 }} />
      <input
        value={value} onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') create(); }}
        placeholder="Add a task to this stage..."
        disabled={creating}
        style={{
          flex: 1, border: 'none', background: 'transparent',
          fontSize: 13, color: T.text, outline: 'none', fontFamily: 'inherit',
          opacity: creating ? 0.5 : 1,
        }}
      />
      <select value={duration} onChange={e => setDuration(Number(e.target.value))} style={{
        border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: '3px 6px',
        fontSize: 11, color: T.textMid, fontFamily: 'inherit', background: '#fff',
      }}>
        <option value={15}>15m</option>
        <option value={30}>30m</option>
        <option value={60}>1h</option>
        <option value={120}>2h</option>
        <option value={240}>4h</option>
      </select>
      {value.trim() && (
        <button onClick={create} disabled={creating} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: T.amber, color: '#fff', cursor: 'pointer',
          fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
        }}>Add</button>
      )}
    </div>
  );
};

// ─── Add Stage Input ────────────────────────────────────────────────────────

const AddStageInput = ({ goalId, nextOrder, onCreated }) => {
  const [value, setValue] = useState('');
  const [adding, setAdding] = useState(false);

  const add = async () => {
    if (!value.trim()) return;
    setAdding(true);
    await supabase.from('milestones').insert({
      goal_id: goalId,
      title: value.trim(),
      sort_order: nextOrder,
      done: false,
      is_active: false,
      stage_status: 'not_started',
    });
    setValue('');
    setAdding(false);
    onCreated();
  };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
      borderRadius: 10, border: '2px dashed rgba(0,0,0,0.06)', marginTop: 12,
    }}>
      <Plus size={14} color={T.textLight} />
      <input
        value={value} onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') add(); }}
        placeholder="Add a new stage..."
        disabled={adding}
        style={{
          flex: 1, border: 'none', background: 'transparent',
          fontSize: 13, color: T.text, outline: 'none', fontFamily: 'inherit',
        }}
      />
      {value.trim() && (
        <button onClick={add} disabled={adding} style={{
          padding: '4px 12px', borderRadius: 6, border: 'none',
          background: T.blue, color: '#fff', cursor: 'pointer',
          fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
        }}>Add Stage</button>
      )}
    </div>
  );
};

// ─── Stage Section ──────────────────────────────────────────────────────────

const StageSection = ({ stage, tasks, goalId, roleId, onUpdate, triggerScheduler }) => {
  const isActive = stage.is_active || stage.stage_status === 'active';
  const isCompleted = stage.stage_status === 'completed' || stage.done;
  const isGhost = !isActive && !isCompleted;
  const [expanded, setExpanded] = useState(isActive);

  const stageTasks = tasks.filter(t => t.milestone_id === stage.id);
  const doneTasks = stageTasks.filter(t => t.done || t.status === 'done');
  const activeTasks = stageTasks.filter(t => !t.done && t.status !== 'done');

  const completeStage = async () => {
    await supabase.from('milestones').update({
      done: true, stage_status: 'completed', is_active: false,
      done_at: new Date().toISOString(),
    }).eq('id', stage.id);
    onUpdate();
    if (triggerScheduler) triggerScheduler();
  };

  const activateStage = async () => {
    await supabase.from('milestones').update({
      is_active: true, stage_status: 'active',
    }).eq('id', stage.id);
    onUpdate();
    if (triggerScheduler) triggerScheduler();
  };

  const statusColor = isCompleted ? T.green : isActive ? T.blue : T.textLight;

  return (
    <div style={{
      borderRadius: 12, overflow: 'hidden',
      border: isGhost ? '1px dashed rgba(0,0,0,0.08)' : `1px solid rgba(0,0,0,0.06)`,
      opacity: isGhost ? 0.6 : 1,
      background: isActive ? 'rgba(74,158,204,0.03)' : 'transparent',
    }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '12px 16px', border: 'none', background: 'transparent',
          cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        {expanded ? <ChevronDown size={14} color={statusColor} /> : <ChevronRight size={14} color={statusColor} />}
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: isCompleted ? T.green : isActive ? T.blue : 'rgba(0,0,0,0.08)',
        }} />
        <span style={{ flex: 1, textAlign: 'left', fontSize: 13, fontWeight: 600, color: isGhost ? T.textMuted : T.text }}>
          {stage.title}
        </span>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          {doneTasks.length}/{stageTasks.length}
        </span>
        {isActive && activeTasks.length === 0 && stageTasks.length > 0 && (
          <button onClick={(e) => { e.stopPropagation(); completeStage(); }} style={{
            padding: '3px 10px', borderRadius: 6, border: 'none',
            background: T.green, color: '#fff', cursor: 'pointer',
            fontSize: 10, fontWeight: 600, fontFamily: 'inherit',
          }}>Complete Stage</button>
        )}
        {isGhost && !isCompleted && (
          <button onClick={(e) => { e.stopPropagation(); activateStage(); }} style={{
            padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.10)',
            background: 'transparent', color: T.textMid, cursor: 'pointer',
            fontSize: 10, fontWeight: 500, fontFamily: 'inherit',
          }}>Activate</button>
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} transition={springs.gentle}
            style={{ overflow: 'hidden' }}
          >
            <div style={{ padding: '0 16px 14px' }}>
              {stageTasks.length === 0 && (
                <div style={{ padding: '12px 0', fontSize: 12, color: T.textMuted, textAlign: 'center' }}>
                  No tasks in this stage yet.
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {stageTasks.map(t => (
                  <div key={t.id} style={{
                    opacity: isGhost ? 0.55 : 1,
                    ...(isGhost ? {
                      borderRadius: 10,
                      border: '1px dashed rgba(0,0,0,0.10)',
                      background: 'rgba(0,0,0,0.01)',
                    } : {}),
                  }}>
                    <TaskRow task={t} onToggle={() => { onUpdate(); if (triggerScheduler) triggerScheduler(); }} showEta />
                  </div>
                ))}
              </div>
              {isActive && (
                <StageTaskInput stageId={stage.id} goalId={goalId} roleId={roleId}
                  onCreated={() => { onUpdate(); if (triggerScheduler) triggerScheduler(); }} />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Team Panel ─────────────────────────────────────────────────────────────

const teamIconBtn = {
  padding: 3, border: 'none', background: 'none', cursor: 'pointer',
  display: 'flex', alignItems: 'center',
};

const TeamPanel = ({ goal, onUpdate }) => {
  const team = (goal.goal_members || []).filter(m => m.member);
  const [adding, setAdding] = useState(false);
  const [members, setMembers] = useState(() => cachedMembers() || []);

  useEffect(() => { getMembers().then(setMembers); }, []);

  const memberIds = new Set(team.map(m => m.member.id));
  const addable = members.filter(m => !memberIds.has(m.id));
  const sorted = [...team].sort(
    (a, b) => (a.member_role === 'lead' ? 0 : 1) - (b.member_role === 'lead' ? 0 : 1)
  );

  const add = async (userId) => {
    setAdding(false);
    await supabase.from('goal_members').insert({
      goal_id: goal.id, user_id: userId, member_role: 'member',
    });
    onUpdate();
  };
  const remove = async (rowId) => {
    await supabase.from('goal_members').delete().eq('id', rowId);
    onUpdate();
  };
  const toggleLead = async (row) => {
    await supabase.from('goal_members')
      .update({ member_role: row.member_role === 'lead' ? 'member' : 'lead' })
      .eq('id', row.id);
    onUpdate();
  };

  return (
    <Glass style={{ padding: 18, marginTop: 12 }} hover={false}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Team
        </span>
        <button onClick={() => setAdding(a => !a)} title="Add member" style={{
          width: 22, height: 22, borderRadius: 6, border: '1px solid rgba(0,0,0,0.08)',
          background: adding ? T.amberLight : 'transparent', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <UserPlus size={12} color={adding ? T.amber : T.textMid} />
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map(row => (
          <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Avatar profile={row.member} size={24} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: T.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName(row.member)}
              </div>
              {row.member_role === 'lead' && (
                <div style={{ fontSize: 9, fontWeight: 700, color: T.amber, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                  Lead
                </div>
              )}
            </div>
            <button onClick={() => toggleLead(row)} title={row.member_role === 'lead' ? 'Remove lead' : 'Make lead'} style={teamIconBtn}>
              <Crown size={12} color={row.member_role === 'lead' ? T.amber : T.textLight} />
            </button>
            <button onClick={() => remove(row.id)} title="Remove from team" style={teamIconBtn}>
              <X size={12} color={T.textLight} />
            </button>
          </div>
        ))}
        {team.length === 0 && !adding && (
          <div style={{ fontSize: 12, color: T.textMuted }}>No one on this project yet.</div>
        )}
      </div>

      {adding && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 220, overflowY: 'auto' }}>
          {addable.map(m => (
            <button key={m.id} onClick={() => add(m.id)} style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '6px', borderRadius: 7, border: 'none', background: 'transparent',
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: T.text, textAlign: 'left',
            }}>
              <Avatar profile={m} size={22} />
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName(m)}
              </span>
              <Plus size={12} color={T.textLight} />
            </button>
          ))}
          {addable.length === 0 && (
            <div style={{ fontSize: 11, color: T.textMuted, padding: '4px 0' }}>Everyone is already on the team.</div>
          )}
        </div>
      )}
    </Glass>
  );
};

// ─── Main ProjectDetailView ─────────────────────────────────────────────────

export const ProjectDetailView = ({ project, milestones, tasks, onBack, onUpdate, triggerScheduler }) => {
  const stages = milestones.filter(m => m.goal_id === project.id).sort((a, b) => a.sort_order - b.sort_order);
  const projectTasks = tasks.filter(t => t.goal_id === project.id);
  const unstagedTasks = projectTasks.filter(t => !t.milestone_id);
  const doneTasks = projectTasks.filter(t => t.done || t.status === 'done');
  const progress = projectTasks.length > 0 ? Math.round((doneTasks.length / projectTasks.length) * 100) : 0;
  const r = project.roles?.slug ? ROLES[project.roles.slug] : null;
  const prio = PRIORITY_MAP[project.priority] || PRIORITY_MAP[3];

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={onBack} style={{
          display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px',
          borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)', background: 'transparent',
          cursor: 'pointer', color: T.textMid, fontSize: 13, fontFamily: 'inherit',
        }}>
          <ArrowLeft size={14} /> Back
        </button>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'start' }}>
        {/* Main content */}
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: T.text, letterSpacing: '-0.03em', marginBottom: 8 }}>
            {project.title}
          </h1>
          {project.description && (
            <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6, marginBottom: 16 }}>
              {project.description}
            </p>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
            {r && <RoleBadge roleSlug={project.roles.slug} />}
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
              color: prio.color, background: `${prio.color}15`,
            }}>{prio.label}</span>
            <span style={{ fontSize: 12, color: T.textMuted }}>
              {doneTasks.length}/{projectTasks.length} tasks complete
            </span>
          </div>

          {/* Progress */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: T.textMuted }}>Progress</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: r?.color || T.amber }}>{progress}%</span>
            </div>
            <ProgressBar value={progress} color={r?.color || T.amber} height={6} />
          </div>

          {/* Stage Timeline */}
          <StageTimeline stages={stages} />

          {/* Stage Sections */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {stages.map(stage => (
              <StageSection
                key={stage.id}
                stage={stage}
                tasks={tasks}
                goalId={project.id}
                roleId={project.role_id}
                onUpdate={onUpdate}
                triggerScheduler={triggerScheduler}
              />
            ))}

            {/* Unstaged tasks */}
            {unstagedTasks.length > 0 && (
              <div style={{
                borderRadius: 12, border: '1px solid rgba(0,0,0,0.06)', padding: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.textMid }}>Unstaged Tasks</span>
                  <span style={{ fontSize: 11, color: T.textMuted }}>{unstagedTasks.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {unstagedTasks.map(t => (
                    <TaskRow key={t.id} task={t} onToggle={() => { onUpdate(); if (triggerScheduler) triggerScheduler(); }} showEta />
                  ))}
                </div>
              </div>
            )}

            {/* Add Stage */}
            <AddStageInput goalId={project.id} nextOrder={stages.length} onCreated={onUpdate} />
          </div>
        </div>

        {/* Sidebar metadata */}
        <div style={{ width: 240, flexShrink: 0 }}>
          <Glass style={{ padding: 18 }} hover={false}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</span>
                <div style={{ fontSize: 13, color: T.text, fontWeight: 500, marginTop: 4 }}>
                  {project.status === 'active' ? 'Active' : project.status}
                </div>
              </div>
              {project.deadline && (
                <div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Deadline</span>
                  <div style={{ fontSize: 13, color: new Date(project.deadline) < new Date() ? T.red : T.text, fontWeight: 500, marginTop: 4 }}>
                    {new Date(project.deadline).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                </div>
              )}
              <div>
                <span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stages</span>
                <div style={{ fontSize: 13, color: T.text, marginTop: 4 }}>
                  {stages.filter(s => s.stage_status === 'completed' || s.done).length}/{stages.length} completed
                </div>
              </div>
              <div>
                <span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tasks</span>
                <div style={{ fontSize: 13, color: T.text, marginTop: 4 }}>
                  {doneTasks.length}/{projectTasks.length} done
                </div>
              </div>
            </div>
          </Glass>

          {/* Team */}
          <TeamPanel goal={project} onUpdate={onUpdate} />

          {/* Actions */}
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {project.project_type !== 'template' && (
              <button onClick={async () => {
                await supabase.from('goals').update({ project_type: 'template' }).eq('id', project.id);
                onUpdate();
              }} style={{
                padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
                background: 'transparent', cursor: 'pointer', fontSize: 12, fontWeight: 500,
                color: T.textMid, fontFamily: 'inherit', width: '100%', textAlign: 'left',
              }}>
                Save as Template
              </button>
            )}
            {project.project_type === 'template' && (
              <div style={{ fontSize: 10, fontWeight: 600, color: T.violet, padding: '4px 8px', borderRadius: 6, background: T.violetLight, textAlign: 'center' }}>
                Template
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
